import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { io, type Socket } from "socket.io-client";
import type { RootState } from "@/redux/store";
import { setTotalUnreadMessages } from "@/redux/slices/notificationSlice";
import {
  acceptUserChatMessage, acceptUserSocketMessage, deleteMessage, editMessage, getAllVendor, getChatMessages, getUnreadChatCount,
  getUserChats, isVerifiedUserChat, markChatAsRead, registerUserChat,
  sendMediaMessage, sendMessage, startChat,
} from "@/utils/UserChat";
import { getPayloadChatId, readMarketplaceConversation } from "@/utils/conversationBoundary";
import { useChatNotifications } from "@/hook/useChatNotifications";
import {
  clockTime, entityId, errorMessage, mergeMessages, messageFromResponse, normalizeMessage, reconcileHistory,
  preview, record, validDate, type ChatMessage, type UserConversation,
} from "./userChatData";

const SOCKET_URL = "https://ilosiwaju-mbaay-2025.com";
const formatConversation = (raw: unknown, id: string): UserConversation | null => {
  const chat = readMarketplaceConversation(raw, { role: "user", id });
  if (!chat) return null;
  const last = acceptUserChatMessage(chat._id, chat.raw.lastMessage) ? normalizeMessage(chat.raw.lastMessage, id, chat._id) : null;
  const timestamp = last?.timestamp || validDate(chat.raw.updatedAt || chat.raw.createdAt);
  const unavailable = !chat.counterpart.name;
  return { _id: chat._id, participantId: chat.counterpart.id, name: chat.counterpart.name || "Vendor details unavailable", avatar: chat.counterpart.avatar || undefined, online: chat.counterpart.online, timestamp, time: clockTime(timestamp), lastMessage: last ? preview(last) : chat.raw.lastMessage ? "Preview unavailable" : "No messages yet", unread: typeof chat.raw.unreadCount === "number" ? Math.max(0, chat.raw.unreadCount) : 0, pinned: false, unavailable };
};

export const useUserMarketplaceChat = () => {
  const user = useSelector((state: RootState) => state.user.user);
  const token = useSelector((state: RootState) => state.user.token);
  const accountId = user?._id || "";
  const dispatch = useDispatch();
  const notifications = useChatNotifications({ channel: "marketplace", role: "user", id: accountId });
  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;
  const [chats, setChats] = useState<UserConversation[]>([]);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [histories, setHistories] = useState<Record<string, ChatMessage[]>>({});
  const [loadingChats, setLoadingChats] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState<Record<string, boolean>>({});
  const [historyErrors, setHistoryErrors] = useState<Record<string, string | null>>({});
  const [chatError, setChatError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [diagnostic, setDiagnostic] = useState<string | null>(null);
  const [sending, setSending] = useState<Record<string, boolean>>({});
  const [typing, setTyping] = useState<Record<string, boolean>>({});
  const [connection, setConnection] = useState<"connecting" | "connected" | "offline">("connecting");
  const sessionRef = useRef(accountId);
  const mountedRef = useRef(true);
  const lifecycleRef = useRef(0);
  const activeRef = useRef(selectedChat);
  const chatsRef = useRef(chats);
  const historyRef = useRef(histories);
  const socketRef = useRef<Socket | null>(null);
  const joinedRef = useRef(new Set<string>());
  const deletedRef = useRef(new Map<string, Set<string>>());
  const requestRef = useRef(new Map<string, number>());
  const readRequestsRef = useRef(new Set<string>());
  const queuedReadRef = useRef(new Set<string>());
  const sendLocksRef = useRef(new Set<string>());
  const seenRef = useRef(new Set<string>());
  const profilesRef = useRef(new Map<string, { name: string; avatar?: string }>());
  const listSequenceRef = useRef(0);
  const typingTimersRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());
  const stopTypingRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const callbacksRef = useRef<{ refresh: () => Promise<void>; history: (id: string) => Promise<void>; read: (id: string) => Promise<void> } | null>(null);
  sessionRef.current = accountId; activeRef.current = selectedChat; chatsRef.current = chats; historyRef.current = histories;

  const refreshUnread = useCallback(async () => {
    if (!accountId || !mountedRef.current) return;
    const lifecycle = lifecycleRef.current;
    try {
      const result = await getUnreadChatCount(accountId);
      if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return;
      if (result.countAvailable) dispatch(setTotalUnreadMessages(result.count));
      setChats(previous => previous.map(chat => ({ ...chat, unread: result.countAvailable ? result.chats[chat._id] || 0 : chat.unread })));
    } catch { /* History/list errors carry retry UI; unread polling keeps the previous badge. */ }
  }, [accountId, dispatch]);

  const refreshChats = useCallback(async () => {
    if (!mountedRef.current) return;
    if (!accountId || !token) { setLoadingChats(false); return; }
    const lifecycle = lifecycleRef.current;
    const sequence = ++listSequenceRef.current;
    try {
      const response = await getUserChats();
      if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current || sequence !== listSequenceRef.current || response.stale === true) return;
      let formatted = response.chats.map(raw => formatConversation(raw, accountId)).filter((chat): chat is UserConversation => !!chat);
      if (formatted.some(chat => chat.unavailable && !profilesRef.current.has(chat.participantId))) {
        try {
          const directory = await getAllVendor();
          if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current || sequence !== listSequenceRef.current) return;
          if (Array.isArray(directory.vendors)) directory.vendors.forEach(value => {
            const vendor = record(value); const id = entityId(vendor); const name = typeof vendor.storeName === "string" ? vendor.storeName.trim() : "";
            if (id && name) profilesRef.current.set(id, { name, avatar: typeof vendor.businessLogo === "string" ? vendor.businessLogo : typeof vendor.avatar === "string" ? vendor.avatar : undefined });
          });
        } catch { /* A verified counterpart can still be messaged when its public profile is unavailable. */ }
      }
      formatted = formatted.map(chat => {
        const profile = profilesRef.current.get(chat.participantId);
        return chat.unavailable && profile ? { ...chat, name: profile.name, avatar: profile.avatar || chat.avatar, unavailable: false } : chat;
      });
      response.chats.forEach(raw => {
        const data = record(raw); const last = record(data.lastMessage); const id = entityId(last); const chatId = entityId(data);
        if (id && chatId) { seenRef.current.add(`${chatId}:${id}`); notificationsRef.current.rememberMessages([{ id, chatId }]); }
      });
      if (activeRef.current && !formatted.some(chat => chat._id === activeRef.current)) { activeRef.current = null; setSelectedChat(null); }
      setChats(previous => formatted.map(chat => {
        const existing = previous.find(item => item._id === chat._id);
        const local = historyRef.current[chat._id]?.at(-1);
        return { ...chat, pinned: existing?.pinned || false, ...(local && local.timestamp > chat.timestamp ? { timestamp: local.timestamp, time: local.time, lastMessage: preview(local) } : {}) };
      }));
      const malformed = response.rejectedConversations.filter(item => item.reason !== "customer_care");
      setDiagnostic(malformed.length ? `${malformed.length} conversation${malformed.length === 1 ? "" : "s"} could not be verified because participant information is missing or invalid. Please contact Customer Care if a conversation is missing.` : null);
      setChatError(null);
      void refreshUnread();
    } catch (error) {
      if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current && sequence === listSequenceRef.current) setChatError(errorMessage(error, "Messages could not be loaded. Try again."));
    } finally { if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current && sequence === listSequenceRef.current) setLoadingChats(false); }
  }, [accountId, token, refreshUnread]);

  const markRead = useCallback(async (chatId: string) => {
    if (!accountId || !mountedRef.current || activeRef.current !== chatId || document.visibilityState !== "visible") return;
    if (readRequestsRef.current.has(chatId)) { queuedReadRef.current.add(chatId); return; }
    const lifecycle = lifecycleRef.current;
    readRequestsRef.current.add(chatId);
    try {
      do {
        queuedReadRef.current.delete(chatId);
        await markChatAsRead(chatId, accountId);
        if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return;
        setChats(previous => previous.map(chat => chat._id === chatId ? { ...chat, unread: 0 } : chat));
        await refreshUnread();
      } while (queuedReadRef.current.has(chatId) && sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current && activeRef.current === chatId && document.visibilityState === "visible");
    } catch (error) { if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current) setActionError(errorMessage(error, "Read state could not be updated.")); }
    finally { if (lifecycleRef.current === lifecycle) { readRequestsRef.current.delete(chatId); queuedReadRef.current.delete(chatId); } }
  }, [accountId, refreshUnread]);

  const loadHistory = useCallback(async (chatId: string) => {
    if (!accountId || !mountedRef.current || !isVerifiedUserChat(chatId)) return;
    const lifecycle = lifecycleRef.current;
    const snapshot = new Map((historyRef.current[chatId] || []).map(message => [message._id, message]));
    const sequence = (requestRef.current.get(chatId) || 0) + 1;
    requestRef.current.set(chatId, sequence);
    setLoadingHistory(previous => ({ ...previous, [chatId]: true }));
    try {
      const response = await getChatMessages(chatId);
      if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current || requestRef.current.get(chatId) !== sequence || !isVerifiedUserChat(chatId)) return;
      const messages = response.messages.map(raw => normalizeMessage(raw, accountId, chatId)).filter((message): message is ChatMessage => !!message && !deletedRef.current.get(chatId)?.has(message._id));
      messages.forEach(message => seenRef.current.add(`${chatId}:${message._id}`));
      notificationsRef.current.rememberMessages(messages.map(message => ({ id: message._id, chatId })));
      // Preserve socket arrivals/edits that landed while the history request was running.
      setHistories(previous => ({ ...previous, [chatId]: reconcileHistory(messages, previous[chatId] || [], snapshot) }));
      setHistoryErrors(previous => ({ ...previous, [chatId]: null }));
      if (activeRef.current === chatId) await markRead(chatId);
    } catch (error) {
      if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current && requestRef.current.get(chatId) === sequence) setHistoryErrors(previous => ({ ...previous, [chatId]: errorMessage(error, "This conversation could not be loaded. Try again.") }));
    } finally { if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current && requestRef.current.get(chatId) === sequence) setLoadingHistory(previous => ({ ...previous, [chatId]: false })); }
  }, [accountId, markRead]);
  callbacksRef.current = { refresh: refreshChats, history: loadHistory, read: markRead };

  useEffect(() => {
    mountedRef.current = true; lifecycleRef.current += 1; sessionRef.current = accountId;
    const historyRequests = requestRef.current;
    setChats([]); setHistories({}); setSelectedChat(null); setLoadingChats(!!accountId); setChatError(null); setActionError(null); setDiagnostic(null); setHistoryErrors({}); setLoadingHistory({}); setSending({}); setTyping({});
    historyRef.current = {}; chatsRef.current = []; activeRef.current = null;
    deletedRef.current.clear(); requestRef.current.clear(); seenRef.current.clear(); profilesRef.current.clear(); sendLocksRef.current.clear(); readRequestsRef.current.clear(); queuedReadRef.current.clear();
    void callbacksRef.current?.refresh();
    return () => { mountedRef.current = false; lifecycleRef.current += 1; sessionRef.current = ""; activeRef.current = null; listSequenceRef.current += 1; historyRequests.clear(); };
  }, [accountId]);

  useEffect(() => {
    if (!accountId || !token) return;
    const socket = io(SOCKET_URL, { auth: { token: `Bearer ${token}` }, reconnection: true });
    socketRef.current = socket; setConnection("connecting");
    const join = () => {
      joinedRef.current.clear();
      for (const chat of chatsRef.current) if (isVerifiedUserChat(chat._id)) { socket.emit("joinChat", chat._id); joinedRef.current.add(chat._id); }
      setConnection("connected"); void callbacksRef.current?.refresh();
      if (activeRef.current) void callbacksRef.current?.history(activeRef.current);
    };
    const incoming = (raw: unknown) => {
      const chatId = getPayloadChatId(raw);
      if (!chatId || !acceptUserSocketMessage(raw)) return;
      const message = normalizeMessage(raw, accountId, chatId);
      if (!message || deletedRef.current.get(chatId)?.has(message._id)) return;
      const key = `${chatId}:${message._id}`; const isNew = !seenRef.current.has(key);
      seenRef.current.add(key);
      setHistories(previous => ({ ...previous, [chatId]: mergeMessages(previous[chatId] || [], [message]) }));
      setChats(previous => previous.map(chat => chat._id === chatId ? { ...chat, ...(message.timestamp >= chat.timestamp ? { lastMessage: preview(message), timestamp: message.timestamp, time: message.time } : {}), unread: isNew && !message.sent ? chat.unread + 1 : chat.unread } : chat));
      if (isNew && !message.sent) {
        notificationsRef.current.notifyIncoming({ id: message._id, chatId, senderId: message.senderId });
        if (activeRef.current === chatId) void callbacksRef.current?.read(chatId);
        else void refreshUnread();
      }
    };
    const edited = (raw: unknown) => {
      const data = record(raw); const id = entityId(data); const room = getPayloadChatId(raw);
      if (!id) return;
      const chatId = room || Object.keys(historyRef.current).find(chat => historyRef.current[chat].some(message => message._id === id));
      if (!chatId || !isVerifiedUserChat(chatId)) return;
      setHistories(previous => ({ ...previous, [chatId]: (previous[chatId] || []).map(message => message._id === id ? { ...message, content: typeof data.content === "string" ? data.content : typeof data.text === "string" ? data.text : message.content, isEdited: true } : message) }));
      void callbacksRef.current?.refresh();
    };
    const deleted = (raw: unknown) => {
      const data = record(raw); const id = entityId(data) || entityId(data.messageId); const chatId = getPayloadChatId(raw);
      if (!id || !chatId || !isVerifiedUserChat(chatId)) return;
      if (!deletedRef.current.has(chatId)) deletedRef.current.set(chatId, new Set());
      deletedRef.current.get(chatId)!.add(id);
      setHistories(previous => ({ ...previous, [chatId]: (previous[chatId] || []).filter(message => message._id !== id) }));
      void callbacksRef.current?.refresh();
    };
    const started = (raw: unknown) => {
      const registered = registerUserChat(raw); if (!registered) return;
      const chat = formatConversation(raw, accountId); if (!chat) return;
      setChats(previous => previous.some(item => item._id === chat._id) ? previous : [chat, ...previous]);
    };
    const typingEvent = (raw: unknown) => {
      const data = record(raw); const chatId = getPayloadChatId(raw); const senderId = entityId(data.sender);
      if (!chatId || senderId === accountId || !acceptUserChatMessage(chatId, { ...data, sender: senderId })) return;
      setTyping(previous => ({ ...previous, [chatId]: true }));
      const timer = typingTimersRef.current.get(chatId); if (timer) clearTimeout(timer);
      typingTimersRef.current.set(chatId, setTimeout(() => setTyping(previous => ({ ...previous, [chatId]: false })), 5000));
    };
    const stopTyping = (raw: unknown) => { const chatId = getPayloadChatId(raw); if (chatId && isVerifiedUserChat(chatId)) setTyping(previous => ({ ...previous, [chatId]: false })); };
    const disconnected = () => { setConnection("offline"); setTyping({}); };
    socket.on("connect", join); socket.on("disconnect", disconnected); socket.on("connect_error", disconnected);
    socket.on("newMessage", incoming); socket.on("messageEdited", edited); socket.on("messageDeleted", deleted); socket.on("chatStarted", started); socket.on("typing", typingEvent); socket.on("stopTyping", stopTyping);
    const typingTimers = typingTimersRef.current;
    return () => {
      socket.removeAllListeners(); socket.disconnect(); if (socketRef.current === socket) socketRef.current = null;
      joinedRef.current.clear(); typingTimers.forEach(clearTimeout); typingTimers.clear();
      if (stopTypingRef.current) clearTimeout(stopTypingRef.current);
    };
  }, [accountId, token, refreshUnread]);

  useEffect(() => {
    const boundaryChanged = () => {
      const allowed = new Set(chatsRef.current.filter(chat => isVerifiedUserChat(chat._id)).map(chat => chat._id));
      setChats(previous => previous.filter(chat => allowed.has(chat._id)));
      setHistories(previous => Object.fromEntries(Object.entries(previous).filter(([id]) => allowed.has(id))));
      if (activeRef.current && !allowed.has(activeRef.current)) { activeRef.current = null; setSelectedChat(null); }
      for (const id of joinedRef.current) if (!allowed.has(id)) { socketRef.current?.emit("leaveChat", id); joinedRef.current.delete(id); }
      void callbacksRef.current?.refresh();
    };
    const stored = (event: StorageEvent) => { if (!event.key || event.key.startsWith(`mbaay:customer-care:user:${accountId}:`)) boundaryChanged(); };
    window.addEventListener("mbaay:conversation-boundary-change", boundaryChanged);
    window.addEventListener("storage", stored);
    return () => { window.removeEventListener("mbaay:conversation-boundary-change", boundaryChanged); window.removeEventListener("storage", stored); };
  }, [accountId]);

  useEffect(() => {
    const socket = socketRef.current; if (!socket?.connected) return;
    const next = new Set(chats.filter(chat => isVerifiedUserChat(chat._id)).map(chat => chat._id));
    joinedRef.current.forEach(id => { if (!next.has(id)) socket.emit("leaveChat", id); });
    next.forEach(id => { if (!joinedRef.current.has(id)) socket.emit("joinChat", id); });
    joinedRef.current = next;
  }, [chats]);

  useEffect(() => {
    if (!accountId || !token) return;
    const interval = setInterval(() => { void callbacksRef.current?.refresh(); }, 30000);
    const visible = () => { if (document.visibilityState === "visible") { void callbacksRef.current?.refresh(); if (activeRef.current) void callbacksRef.current?.history(activeRef.current); } };
    document.addEventListener("visibilitychange", visible);
    return () => { clearInterval(interval); document.removeEventListener("visibilitychange", visible); };
  }, [accountId, token]);

  const selectChat = useCallback((chatId: string | null) => {
    const old = activeRef.current;
    if (old) socketRef.current?.emit("stopTyping", { chatId: old, sender: accountId });
    if (stopTypingRef.current) clearTimeout(stopTypingRef.current);
    if (chatId && !isVerifiedUserChat(chatId)) { setActionError("This conversation could not be verified."); return; }
    activeRef.current = chatId; setSelectedChat(chatId); setActionError(null);
    if (chatId) void callbacksRef.current?.history(chatId);
  }, [accountId]);

  const newChat = async (receiverId: string) => {
    const lifecycle = lifecycleRef.current;
    if (!mountedRef.current) return null;
    try {
      const response = await startChat(receiverId);
      if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return null;
      const chat = formatConversation(response.chat, accountId);
      if (!chat) throw new Error("The selected vendor's conversation could not be verified.");
      setChats(previous => previous.some(item => item._id === chat._id) ? previous : [chat, ...previous]);
      selectChat(chat._id); return chat._id;
    } catch (error) { if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current) setActionError(errorMessage(error, "A conversation could not be started.")); return null; }
  };

  const send = async (content: string, files: File[] = []) => {
    const lifecycle = lifecycleRef.current;
    const chatId = activeRef.current;
    if (!chatId || !accountId || !mountedRef.current || sendLocksRef.current.has(chatId)) return false;
    if (!content.trim() && !files.length) return false;
    sendLocksRef.current.add(chatId); setSending(previous => ({ ...previous, [chatId]: true })); setActionError(null);
    const id = `local-${crypto.randomUUID()}`; const timestamp = new Date();
    const urls = files.map(file => URL.createObjectURL(file));
    const images = files.flatMap((file, index) => file.type.startsWith("image/") ? [urls[index]] : []);
    const videoIndex = files.findIndex(file => file.type.startsWith("video/"));
    const documents = files.flatMap((file, index) => !file.type.startsWith("image/") && !file.type.startsWith("video/") ? [{ url: urls[index], name: file.name }] : []);
    const optimistic: ChatMessage = { _id: id, clientKey: id, chatId, senderId: accountId, content: content.trim(), images: images.length ? images : undefined, video: videoIndex >= 0 ? urls[videoIndex] : undefined, documents: documents.length ? documents : undefined, time: clockTime(timestamp), timestamp, sent: true, type: images.length ? "image" : videoIndex >= 0 ? "video" : documents.length ? "file" : "text", isOptimistic: true, isUploading: !!files.length, status: "sending" };
    const beforeIds = new Set((historyRef.current[chatId] || []).map(message => message._id));
    let accepted = false;
    setHistories(previous => ({ ...previous, [chatId]: mergeMessages(previous[chatId] || [], [optimistic]) }));
    try {
      const response = files.length ? await sendMediaMessage(chatId, files, undefined, content) : await sendMessage(chatId, content);
      accepted = true;
      if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return true;
      const raw = messageFromResponse(response);
      let confirmed = acceptUserChatMessage(chatId, raw) ? normalizeMessage(raw, accountId, chatId) : null;
      if (confirmed && !confirmed.sent) confirmed = null;
      if (!confirmed) {
        // Recover an ID-only acknowledgement from history, never from an arbitrary peer event.
        const history = await getChatMessages(chatId);
        if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return true;
        const normalized = history.messages.map(value => normalizeMessage(value, accountId, chatId)).filter((message): message is ChatMessage => !!message);
        confirmed = normalized.find(message => !beforeIds.has(message._id) && message.sent && message.content === optimistic.content && message.type === optimistic.type && message.timestamp.getTime() >= timestamp.getTime() - 5000) || null;
        setHistories(previous => ({ ...previous, [chatId]: mergeMessages(normalized, previous[chatId] || []) }));
      }
      if (confirmed) {
        seenRef.current.add(`${chatId}:${confirmed._id}`);
        notificationsRef.current.rememberMessages([{ id: confirmed._id, chatId }]);
        setHistories(previous => ({ ...previous, [chatId]: mergeMessages(previous[chatId] || [], [confirmed!], id) }));
      } else {
        setHistories(previous => ({ ...previous, [chatId]: (previous[chatId] || []).filter(message => message._id !== id) }));
        setActionError("The message was accepted, but the server did not return its details. Refresh this conversation to confirm it.");
      }
      void callbacksRef.current?.refresh(); return true;
    } catch (error) {
      if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current) {
        setHistories(previous => ({ ...previous, [chatId]: (previous[chatId] || []).filter(message => message._id !== id) }));
        setActionError(accepted ? "The message was accepted, but its confirmation could not be loaded. Refresh the conversation to check it." : errorMessage(error, "Delivery could not be confirmed. Check the conversation before sending again."));
      }
      return accepted;
    } finally {
      urls.forEach(URL.revokeObjectURL);
      if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current) { sendLocksRef.current.delete(chatId); setSending(previous => ({ ...previous, [chatId]: false })); }
    }
  };

  const changeMessage = async (messageId: string, content?: string) => {
    const lifecycle = lifecycleRef.current;
    const chatId = activeRef.current; if (!chatId || !mountedRef.current) return;
    const existing = historyRef.current[chatId]?.find(message => message._id === messageId);
    if (!existing?.sent || existing.isOptimistic || messageId.startsWith("local-")) return;
    try {
      if (content === undefined) {
        await deleteMessage(messageId, chatId);
        if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return;
        if (!deletedRef.current.has(chatId)) deletedRef.current.set(chatId, new Set()); deletedRef.current.get(chatId)!.add(messageId);
        setHistories(previous => ({ ...previous, [chatId]: (previous[chatId] || []).filter(message => message._id !== messageId) }));
      } else {
        await editMessage(messageId, content, chatId);
        if (sessionRef.current !== accountId || lifecycleRef.current !== lifecycle || !mountedRef.current) return;
        setHistories(previous => ({ ...previous, [chatId]: (previous[chatId] || []).map(message => message._id === messageId ? { ...message, content, isEdited: true } : message) }));
      }
      void callbacksRef.current?.refresh();
    } catch (error) { if (sessionRef.current === accountId && lifecycleRef.current === lifecycle && mountedRef.current) setActionError(errorMessage(error, "The message could not be updated.")); }
  };

  const onTyping = () => {
    const chatId = activeRef.current; if (!chatId || !socketRef.current?.connected) return;
    socketRef.current.emit("typing", { chatId, sender: accountId });
    if (stopTypingRef.current) clearTimeout(stopTypingRef.current);
    stopTypingRef.current = setTimeout(() => socketRef.current?.emit("stopTyping", { chatId, sender: accountId }), 1200);
  };

  const messages = selectedChat ? histories[selectedChat] || [] : [];
  const conversations = chats.map(chat => {
    const last = histories[chat._id]?.at(-1);
    return last && last.timestamp >= chat.timestamp ? { ...chat, lastMessage: preview(last), timestamp: last.timestamp, time: last.time } : chat;
  }).sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.timestamp.getTime() - a.timestamp.getTime());
  return { accountId, token, chats: conversations, selectedChat, messages, loadingChats, loadingMessages: !!(selectedChat && loadingHistory[selectedChat]), chatError, messageError: selectedChat ? historyErrors[selectedChat] : null, actionError, diagnostic, isSending: !!(selectedChat && sending[selectedChat]), isTyping: !!(selectedChat && typing[selectedChat]), connection, notifications, refreshChats, loadHistory, selectChat, newChat, send, changeMessage, onTyping, dismissError: () => setActionError(null), pin: (id: string) => setChats(previous => previous.map(chat => chat._id === id ? { ...chat, pinned: !chat.pinned } : chat)) };
};
